import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { submitQuote } from "@/lib/api";
import { Loader2 } from "lucide-react";

const initial = { name: "", email: "", phone: "", company: "", metal_type: "gold", quantity: "", service: "Gold Refining", notes: "" };

export default function QuoteModal({ open, onOpenChange }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await submitQuote(form);
      toast.success("Quote request submitted. We'll be in touch shortly.");
      setForm(initial);
      onOpenChange(false);
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quote-modal" className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light tracking-tight">Request a Quote</DialogTitle>
          <DialogDescription className="text-[#4B5563]">
            Share your refining requirement — our team responds within one business day.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="q-name">Name</Label>
              <Input id="q-name" data-testid="quote-name" value={form.name} onChange={(e) => update("name", e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="q-phone">Phone</Label>
              <Input id="q-phone" data-testid="quote-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} required />
            </div>
          </div>
          <div>
            <Label htmlFor="q-email">Email</Label>
            <Input id="q-email" type="email" data-testid="quote-email" value={form.email} onChange={(e) => update("email", e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="q-company">Company (optional)</Label>
              <Input id="q-company" data-testid="quote-company" value={form.company} onChange={(e) => update("company", e.target.value)} />
            </div>
            <div>
              <Label>Metal type</Label>
              <Select value={form.metal_type} onValueChange={(v) => update("metal_type", v)}>
                <SelectTrigger data-testid="quote-metal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="mixed">Mixed / Scrap</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="q-qty">Approx. quantity</Label>
              <Input id="q-qty" placeholder="e.g. 5 kg" data-testid="quote-quantity" value={form.quantity} onChange={(e) => update("quantity", e.target.value)} />
            </div>
            <div>
              <Label>Service</Label>
              <Select value={form.service} onValueChange={(v) => update("service", v)}>
                <SelectTrigger data-testid="quote-service"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Gold Refining">Gold Refining</SelectItem>
                  <SelectItem value="Silver Refining">Silver Refining</SelectItem>
                  <SelectItem value="Recovery">Precious Metal Recovery</SelectItem>
                  <SelectItem value="Assaying">Assaying & Purity Analysis</SelectItem>
                  <SelectItem value="Bullion">Bullion Manufacturing</SelectItem>
                  <SelectItem value="Consultation">Consultation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="q-notes">Notes (optional)</Label>
            <Textarea id="q-notes" data-testid="quote-notes" rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} />
          </div>
          <Button type="submit" data-testid="quote-submit" disabled={loading} className="btn-gold h-11 mt-1">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Request"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
